import React from 'react';
import { documentToReactComponents } from '@contentful/rich-text-react-renderer';
import type { Document } from '@contentful/rich-text-types';

import { BUTTON_SIZES, PrimaryButton } from 'js/src/components/common/ui/Button';
import ContentfulImageOrVideo from 'js/src/components/public-pages/ContentfulImageOrVideo';
import { type DeepReadonly, toMutableCopy } from 'js/src/libs/consts';
import type { ContentfulImageOrVideoData } from 'js/src/libs/contentful-utils';
import { getColClassNames, TextWidth } from 'js/src/libs/contentful-utils';

type Props = {
    data: DeepReadonly<{
        paragraph: Document;
        headline: string;
        image: ContentfulImageOrVideoData;
        image_side: string;
        cta_url?: string;
        cta_text?: string;
        poster_for_video: ContentfulImageOrVideoData;
        text_width?: TextWidth;
    }>;
    bgColor: string;
    lazyLoad: boolean;
    isPageHeading?: boolean;
};

const Hero = React.memo(function Hero({ data, bgColor, lazyLoad, isPageHeading = false }: Props) {
    let paragraph = null;
    if (data.paragraph) {
        paragraph = documentToReactComponents(toMutableCopy<Document>(data.paragraph));
    }

    let cta = null;

    const ctaUrl = data.cta_url;
    const ctaText = data.cta_text;

    if (ctaUrl && ctaText) {
        cta = <PrimaryButton href={ctaUrl} size={BUTTON_SIZES.LG} text={ctaText} />;
    }

    const isImageLeft = (data.image_side || '').toString().toLowerCase() === 'left';
    const textWidth = data.text_width || TextWidth.STANDARD;

    const textInner = <div className="font-l5 font-l5--regular">{paragraph}</div>;

    const textBlock = (
        <>
            {isPageHeading ? <h1 className="h3">{data.headline}</h1> : <h3>{data.headline}</h3>}
            {textInner}
            {cta}
        </>
    );

    const imageBlock = (
        <ContentfulImageOrVideo
            data={data.image}
            posterForVideo={data.poster_for_video}
            urlWidth={800}
            style={{ width: '100%' }}
            lazyLoad={lazyLoad}
        />
    );

    const { leftMd, rightMd } = getColClassNames(textWidth, isImageLeft);

    const leftContent = isImageLeft ? imageBlock : textBlock;
    const rightContent = isImageLeft ? textBlock : imageBlock;

    return (
        <div className="contentful-component hero" style={{ backgroundColor: bgColor }}>
            <div className="container">
                <div className="row">
                    <div className={`my-l5-24 ${leftMd} align-self-center`}>
                        <div className="me-l5-md-48">{leftContent}</div>
                    </div>
                    <div className={`my-l5-24 ${rightMd} align-self-center`}>
                        <div className="ms-l5-md-48">{rightContent}</div>
                    </div>
                </div>
            </div>
        </div>
    );
});

export default Hero;
